// @ts-strict-ignore
import { CartLine, CartSnapshot } from "@dashboard/cartVisibility/types";
import { cartVisibilityListUrl } from "@dashboard/cartVisibility/urls";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import CardSpacer from "@dashboard/components/CardSpacer";
import { DateTime } from "@dashboard/components/Date";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import Money from "@dashboard/components/Money";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import { Savebar } from "@dashboard/components/Savebar";
import TableCellAvatar from "@dashboard/components/TableCellAvatar";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import TableHead from "@dashboard/components/TableHead";
import TableRowLink from "@dashboard/components/TableRowLink";
import { OrderDetailsFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import OrderChannelSectionCard from "@dashboard/orders/components/OrderChannelSectionCard";
import OrderCustomer from "@dashboard/orders/components/OrderCustomer";
import { TableBody, TableCell } from "@material-ui/core";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import { FC } from "react";
import { FormattedMessage } from "react-intl";

import { messages } from "./messages";

interface CartVisibilityDetailsPageProps {
  cart: CartSnapshot | undefined;
  loading: boolean;
  promoting: boolean;
  onTakeOver: (token: string) => void;
  onOrderClick: (id: string) => void;
}

const numberOfColumns = 6;

const noop = () => undefined;

export const CartVisibilityDetailsPage: FC<CartVisibilityDetailsPageProps> = ({
  cart,
  loading,
  promoting,
  onTakeOver,
  onOrderClick,
}) => {
  const navigate = useNavigator();
  const lines = (cart?.lines as CartLine[] | null) ?? [];
  const currency = cart?.currency ?? "";
  const draftOrder = cart?.draftOrder;

  const subtotal = lines.reduce((acc, line) => acc + Number(line.unit_price) * line.quantity, 0);

  // Minimal Order-shaped object so we can reuse the exact draft-order sidebar
  // components (OrderCustomer + OrderChannelSectionCard) read-only.
  const orderLike = cart
    ? ({
        id: cart.id,
        user: cart.user ?? null,
        userEmail: cart.email || null,
        billingAddress: cart.billingAddress ?? null,
        shippingAddress: cart.shippingAddress ?? null,
        channel: cart.channel,
        metadata: [],
        deliveryMethod: null,
      } as unknown as OrderDetailsFragment)
    : undefined;

  return (
    <DetailPageLayout>
      <TopNav
        href={cartVisibilityListUrl()}
        title={
          <Box display="flex" alignItems="center" gap={3}>
            <span>
              {cart ? (
                cart.email || <FormattedMessage {...messages.anonymous} />
              ) : (
                <Skeleton style={{ width: "10em" }} />
              )}
            </span>
            <div>
              {cart?.lastChange ? (
                <Text size={3} fontWeight="regular">
                  <DateTime date={cart.lastChange} plain />
                </Text>
              ) : null}
            </div>
          </Box>
        }
      />
      <DetailPageLayout.Content>
        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Title>
              <FormattedMessage {...messages.cartDetails} />
            </DashboardCard.Title>
          </DashboardCard.Header>
          {!loading && !cart ? (
            <DashboardCard.Content>
              <Text color="default2">
                <FormattedMessage {...messages.notFound} />
              </Text>
            </DashboardCard.Content>
          ) : (
            <>
              <ResponsiveTable>
                <TableHead colSpan={numberOfColumns} disabled={loading} items={[]}>
                  <TableCellHeader>
                    <FormattedMessage {...messages.product} />
                  </TableCellHeader>
                  <TableCellHeader>
                    <FormattedMessage {...messages.sku} />
                  </TableCellHeader>
                  <TableCellHeader>
                    <FormattedMessage {...messages.variantName} />
                  </TableCellHeader>
                  <TableCellHeader textAlign="right">
                    <FormattedMessage {...messages.quantity} />
                  </TableCellHeader>
                  <TableCellHeader textAlign="right">
                    <FormattedMessage {...messages.unitPrice} />
                  </TableCellHeader>
                  <TableCellHeader textAlign="right">
                    <FormattedMessage {...messages.lineTotal} />
                  </TableCellHeader>
                </TableHead>
                <TableBody>
                  {renderCollection(
                    cart ? lines : undefined,
                    (line, index) => (
                      <TableRowLink key={line ? `${line.variant_id}-${index}` : "skeleton"}>
                        <TableCellAvatar thumbnail={line?.thumbnail ?? undefined}>
                          {line ? line.product_name : <Skeleton />}
                        </TableCellAvatar>
                        <TableCell>{line ? line.sku || "—" : <Skeleton />}</TableCell>
                        <TableCell>
                          {line ? (
                            line.name && line.name !== line.product_name ? (
                              line.name
                            ) : (
                              "—"
                            )
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell align="right">{line ? line.quantity : <Skeleton />}</TableCell>
                        <TableCell align="right">
                          {line ? (
                            <Money money={{ amount: Number(line.unit_price), currency }} />
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {line ? (
                            <Money
                              money={{
                                amount: Number(line.unit_price) * line.quantity,
                                currency,
                              }}
                            />
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                      </TableRowLink>
                    ),
                    () => (
                      <TableRowLink>
                        <TableCell colSpan={numberOfColumns}>
                          <FormattedMessage {...messages.empty} />
                        </TableCell>
                      </TableRowLink>
                    ),
                  )}
                </TableBody>
              </ResponsiveTable>
              {cart && lines.length !== 0 && (
                <DashboardCard.Content>
                  <Box display="flex" flexDirection="column" gap={2} data-test-id="cart-summary">
                    <Box display="flex" justifyContent="space-between">
                      <Text color="default2">
                        <FormattedMessage {...messages.subtotal} />
                      </Text>
                      <Money money={{ amount: subtotal, currency }} />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Text>
                        <FormattedMessage {...messages.total} />
                      </Text>
                      {cart.totalGross != null ? (
                        <Money money={{ amount: cart.totalGross, currency }} />
                      ) : (
                        <Text>—</Text>
                      )}
                    </Box>
                  </Box>
                </DashboardCard.Content>
              )}
            </>
          )}
        </DashboardCard>
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <OrderChannelSectionCard channel={cart?.channel} />
        <CardSpacer />
        <OrderCustomer
          order={orderLike}
          canEditAddresses={false}
          canEditCustomer={false}
          errors={[]}
          onBillingAddressEdit={noop}
          onShippingAddressEdit={noop}
          onProfileView={noop}
        />
      </DetailPageLayout.RightSidebar>

      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(cartVisibilityListUrl())} />
        {draftOrder ? (
          <Savebar.ConfirmButton
            transitionState="default"
            onClick={() => onOrderClick(draftOrder.id)}
            disabled={!cart}
            data-test-id="open-draft-button"
          >
            <FormattedMessage {...messages.openDraft} values={{ number: draftOrder.number }} />
          </Savebar.ConfirmButton>
        ) : (
          <Savebar.ConfirmButton
            transitionState={promoting ? "loading" : "default"}
            onClick={() => onTakeOver(cart.checkoutToken)}
            disabled={!cart || promoting}
            data-test-id="take-over-button"
          >
            <FormattedMessage {...messages.takeOver} />
          </Savebar.ConfirmButton>
        )}
      </Savebar>
    </DetailPageLayout>
  );
};
